import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import developersApi from '../../../../api/services/developerService';

const EMPTY_FORM = {
  legal_name: '',
  display_name: '',
  registration_number: '',
  phone: '',
  website: '',
  description: '',
};

const OPEN_CASE_STATUSES = new Set(['SUBMITTED', 'UNDER_REVIEW', 'WAITING_FOR_SURVEYS']);

const DOCUMENT_TYPES = [
  ['CERTIFICATE', 'گواهی پایان کار'],
  ['IDENTIFICATION', 'مدارک شناسایی'],
  ['CONTRACT', 'قرارداد'],
  ['LICENCES', 'مجوز'],
  ['MISC', 'سایر مدارک'],
];

const STATUS_LABELS = {
  UNVERIFIED: 'تایید نشده',
  UNDER_REVIEW: 'در حال بررسی',
  ADD_INFO: 'نیازمند اطلاعات بیشتر',
  VERIFIED: 'تایید شده',
  REJECTED: 'رد شده',
  SUSPENDED: 'تعلیق شده',
  DRAFT: 'پیش‌نویس',
  SUBMITTED: 'ارسال شده',
  WAITING_FOR_SURVEYS: 'در انتظار تکمیل نظرسنجی',
  APPROVED: 'تایید شده',
  CANCELLED: 'لغو شده',
};

const LEVEL_LABELS = {
  NONE: 'بدون سطح',
  BASIC: 'پایه',
  MEDIUM: 'متوسط',
  PREMIUM: 'ویژه',
};

function normalizeError(error) {
  const data = error?.response?.data;
  if (!data) return 'ارتباط با سرور برقرار نشد.';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (Array.isArray(data)) return data.join(' ');
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join('، ') : value}`)
    .join(' | ');
}

function formatDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function statusTone(status) {
  if (['VERIFIED', 'APPROVED'].includes(status)) return 'success';
  if (['REJECTED', 'SUSPENDED', 'CANCELLED'].includes(status)) return 'danger';
  if (['UNDER_REVIEW', 'SUBMITTED', 'WAITING_FOR_SURVEYS', 'ADD_INFO'].includes(status)) return 'warning';
  return 'neutral';
}

export default function DeveloperProfilePanel() {
  const [firms, setFirms] = useState([]);
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [cases, setCases] = useState([]);
  const [documentType, setDocumentType] = useState('CERTIFICATE');
  const [documentFile, setDocumentFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const selectedFirm = useMemo(
    () => firms.find((firm) => String(firm.id) === String(selectedFirmId)) || null,
    [firms, selectedFirmId]
  );

  const loadData = useCallback(async (preferredFirmId) => {
    setLoading(true);
    setError('');
    try {
      const loadedFirms = await developersApi.listFirms();
      setFirms(loadedFirms);
      const nextId = preferredFirmId && loadedFirms.some((firm) => String(firm.id) === String(preferredFirmId))
        ? preferredFirmId
        : loadedFirms[0]?.id || '';
      setSelectedFirmId(nextId);
      if (nextId) {
        const loadedCases = await developersApi.listVerificationCases(nextId);
        setCases(loadedCases.filter((item) => String(item.developer) === String(nextId)));
      } else {
        setCases([]);
      }
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedFirm) {
      setForm(EMPTY_FORM);
      return;
    }
    setForm({
      legal_name: selectedFirm.legal_name || '',
      display_name: selectedFirm.display_name || '',
      registration_number: selectedFirm.registration_number || '',
      phone: selectedFirm.phone || '',
      website: selectedFirm.website || '',
      description: selectedFirm.description || '',
    });
  }, [selectedFirm]);

  const changeFirm = async (event) => {
    const firmId = event.target.value;
    setSelectedFirmId(firmId);
    setLoading(true);
    setError('');
    try {
      const loadedCases = await developersApi.listVerificationCases(firmId);
      setCases(loadedCases.filter((item) => String(item.developer) === String(firmId)));
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (action, successMessage) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      setNotice(successMessage);
      await loadData(selectedFirmId);
      return true;
    } catch (err) {
      setError(normalizeError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = (event) => {
    event.preventDefault();
    runAction(
      () => developersApi.updateFirm(selectedFirm.id, form),
      'اطلاعات پروفایل سازنده ذخیره شد.'
    );
  };

  const uploadDocument = async (event) => {
    event.preventDefault();
    if (!documentFile) {
      setError('ابتدا فایل مدرک را انتخاب کنید.');
      return;
    }
    const formData = new FormData();
    formData.append('type', documentType);
    formData.append('file', documentFile);
    const completed = await runAction(
      () => developersApi.uploadFirmDocument(selectedFirm.id, formData),
      'مدرک با موفقیت بارگذاری شد.'
    );
    if (completed) setDocumentFile(null);
  };

  const hasOpenCase = cases.some((item) => OPEN_CASE_STATUSES.has(item.status));
  const canRequestBasic = !hasOpenCase && ['UNVERIFIED', 'REJECTED', 'ADD_INFO'].includes(selectedFirm?.verification_status);
  const canRequestMedium = !hasOpenCase
    && selectedFirm?.verification_status === 'VERIFIED'
    && selectedFirm?.verification_level === 'BASIC';

  if (loading && firms.length === 0) {
    return <div className="developer-profile-panel" dir="rtl">در حال دریافت اطلاعات سازنده...</div>;
  }

  if (!selectedFirm) {
    return (
      <div className="developer-profile-panel developer-empty" dir="rtl">
        <h2>پروفایل سازنده‌ای برای این حساب پیدا نشد</h2>
        <p>ابتدا اطلاعات شرکت را در فرایند ثبت‌نام سازندگان تکمیل کنید.</p>
        <Link className="primary-btn developer-action-btn" to="/developer-register">شروع ثبت‌نام سازنده</Link>
      </div>
    );
  }

  return (
    <div className="developer-profile-panel" dir="rtl">
      <div className="developer-panel-header">
        <div>
          <h2>پروفایل و تایید سازنده</h2>
          <p>اطلاعات شرکت، مدارک و وضعیت درخواست تایید خود را مدیریت کنید.</p>
        </div>
        <div className="developer-status-summary">
          <span className={`developer-status ${statusTone(selectedFirm.verification_status)}`}>
            {STATUS_LABELS[selectedFirm.verification_status] || selectedFirm.verification_status}
          </span>
          <span className="developer-level">سطح: {LEVEL_LABELS[selectedFirm.verification_level] || selectedFirm.verification_level}</span>
        </div>
      </div>

      {firms.length > 1 && (
        <label className="developer-firm-picker">
          <span>پروفایل سازنده</span>
          <select value={selectedFirmId} onChange={changeFirm} disabled={busy}>
            {firms.map((firm) => <option key={firm.id} value={firm.id}>{firm.display_name || firm.legal_name}</option>)}
          </select>
        </label>
      )}

      {error && <div className="developer-message error">{error}</div>}
      {notice && <div className="developer-message success">{notice}</div>}

      <form className="developer-section" onSubmit={saveProfile}>
        <div className="developer-section-heading">
          <div><h3>اطلاعات شرکت</h3><p>این اطلاعات در بررسی درخواست تایید استفاده می‌شود.</p></div>
          <button className="primary-btn developer-action-btn" type="submit" disabled={busy}>ذخیره تغییرات</button>
        </div>
        <div className="developer-form-grid">
          <label><span>نام حقوقی</span><input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} required /></label>
          <label><span>نام نمایشی</span><input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} required /></label>
          <label><span>شماره ثبت</span><input value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} required /></label>
          <label><span>تلفن کاری</span><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></label>
          <label><span>وب‌سایت</span><input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></label>
          <label className="developer-wide-field"><span>توضیحات</span><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        </div>
      </form>

      <section className="developer-section">
        <div className="developer-section-heading">
          <div><h3>مدارک تایید</h3><p>حداقل یک مدرک برای درخواست تایید پایه لازم است.</p></div>
          <span className="developer-count">{selectedFirm.documents?.length || 0} مدرک</span>
        </div>
        <form className="developer-upload-row" onSubmit={uploadDocument}>
          <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} disabled={busy}>
            {DOCUMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <input key={documentFile ? documentFile.name : 'empty'} type="file" onChange={(e) => setDocumentFile(e.target.files?.[0] || null)} disabled={busy} required />
          <button className="primary-btn developer-action-btn" type="submit" disabled={busy}>بارگذاری مدرک</button>
        </form>
        <div className="developer-documents-list">
          {(selectedFirm.documents || []).length === 0 ? (
            <p className="developer-muted">هنوز مدرکی بارگذاری نشده است.</p>
          ) : selectedFirm.documents.map((document) => (
            <a key={document.id} href={document.file_url} target="_blank" rel="noreferrer">
              <span>{DOCUMENT_TYPES.find(([value]) => value === document.type)?.[1] || document.type}</span>
              <small>{formatDate(document.uploaded_at)}</small>
            </a>
          ))}
        </div>
      </section>

      <section className="developer-section">
        <div className="developer-section-heading">
          <div><h3>درخواست تایید</h3><p>پس از تکمیل اطلاعات و مدارک، درخواست را برای تیم بررسی ارسال کنید.</p></div>
          <div className="developer-request-actions">
            {canRequestBasic && (
              <button
                className="primary-btn developer-action-btn"
                type="button"
                disabled={busy || !selectedFirm.documents?.length}
                onClick={() => runAction(
                  () => developersApi.requestBasicVerification(selectedFirm.id),
                  'درخواست تایید پایه ارسال شد.'
                )}
              >
                ارسال درخواست پایه
              </button>
            )}
            {canRequestMedium && (
              <button
                className="primary-btn developer-action-btn"
                type="button"
                disabled={busy}
                onClick={() => runAction(
                  () => developersApi.requestMediumVerification(selectedFirm.id),
                  'درخواست تایید متوسط ارسال شد.'
                )}
              >
                ارسال درخواست متوسط
              </button>
            )}
          </div>
        </div>
        {canRequestBasic && !selectedFirm.documents?.length && (
          <div className="developer-message warning">برای فعال شدن ارسال درخواست، ابتدا یک مدرک بارگذاری کنید.</div>
        )}
        {hasOpenCase && <div className="developer-message warning">یک درخواست باز دارید و نتیجه آن در همین صفحه نمایش داده می‌شود.</div>}

        <div className="developer-case-list">
          {cases.length === 0 ? (
            <p className="developer-muted">هنوز درخواست تاییدی ارسال نشده است.</p>
          ) : cases.map((item) => (
            <article className="developer-case-card" key={item.id}>
              <div className="developer-case-topline">
                <strong>درخواست شماره {item.number}</strong>
                <span className={`developer-status ${statusTone(item.status)}`}>{STATUS_LABELS[item.status] || item.status}</span>
              </div>
              <div className="developer-case-meta">
                <span>سطح: {LEVEL_LABELS[item.level] || item.level || '—'}</span>
                <span>ارسال: {formatDate(item.submitted_at || item.created_at)}</span>
                <span>بررسی‌کننده: {item.assigned_to_display || 'هنوز تعیین نشده'}</span>
                {item.completed_at && <span>پایان بررسی: {formatDate(item.completed_at)}</span>}
              </div>
              {item.decision_notes && <p className="developer-decision-note"><strong>یادداشت بررسی:</strong> {item.decision_notes}</p>}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
